import {AxiosProgressEvent} from 'axios';
import {useCallback, useState} from 'react';

import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {useAuthorizationsStore} from 'presentation/pages/private/authorizations/store/authorizationsStore';
import {readableSize} from 'presentation/utils';
import {cleanFileName} from 'presentation/utils/cleanFileName';
import {useUploadedFilesStore} from '../../store/useUploadedFilesStore';
import {IFile} from '../../types';
import {UseUploadFileProps} from './types';

export function useUploadFile({
	deleteFactory,
	postFactory,
	protocolCode,
	requestCode,
	nuControle,
	showNotification,
}: UseUploadFileProps) {
	const uploadedFiles = useUploadedFilesStore(state => state.uploadedFiles);
	const updateFile = useUploadedFilesStore(state => state.updateFile);
	const removeFilesWithError = useUploadedFilesStore(state => state.removeFilesWithError);
	const addFiles = useUploadedFilesStore(state => state.addFiles);
	const removeFile = useUploadedFilesStore(state => state.removeFile);

	const [deleteState, setDeleteState] = useState<OperationStateType>(
		OperationStateTypesEnum.PENDING,
	);
	const IS_DELETING = deleteState === OperationStateTypesEnum.SUBMITTING;
	const IS_DELETING_ERROR = deleteState === OperationStateTypesEnum.ERROR;
	const IS_UPLOADING = uploadedFiles.some(file => file.uploaded !== true && file.error !== true);

	const randomId = crypto.randomUUID();

	const setAttachSentNotification = useAuthorizationsStore(
		state => state.setAttachSentNotification,
	);

	const blockCloseModal = useCallback(() => {
		setDeleteState(OperationStateTypesEnum.SUBMITTING);
	}, []);

	const handleSetAttachSentNotification = () => {
		setAttachSentNotification(true);
		if (showNotification) {
			return;
		}
		setTimeout(() => {
			setAttachSentNotification(false);
		}, 3000);
	};

	const processUpload = useCallback(
		async (uploadedFile: IFile) => {
			if (uploadedFile.file) {
				const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
					if (progressEvent.total !== undefined) {
						const progress: number = Math.round(
							(progressEvent.loaded * 100) / progressEvent.total,
						);
						updateFile(uploadedFile.id, {progress});
					}
				};

				try {
					const response = await postFactory(onUploadProgress).post({
						file: uploadedFile.file,
						'protocol-code': protocolCode,
						'request-code': requestCode,
						nuControle,
					});

					if (response.nuArquivo) {
						handleSetAttachSentNotification();
						updateFile(uploadedFile.id, {
							uploaded: true,
							id: response.nuArquivo.toString(),
						});
					}

					updateFile(uploadedFile.id, {
						uploaded: true,
						id:
							response.content?.attachmentCode?.toString() ??
							response.content?.attachmentId?.toString(),
					});
				} catch (error) {
					updateFile(uploadedFile.id, {
						error: true,
					});
				}
			}
		},
		[updateFile, nuControle],
	);

	const handleUpload = useCallback(
		(files: File[]) => {
			removeFilesWithError();
			const newUploadedFiles: IFile[] = files.map((file: File) => ({
				file: new File([file], cleanFileName(file.name), {
					type: file.type,
				}),
				id: randomId,
				filename: cleanFileName(file.name),
				readableSize: readableSize(file.size),
				progress: 0,
				uploaded: false,
				error: false,
			}));

			addFiles(newUploadedFiles);
			newUploadedFiles.forEach(processUpload);
		},
		[processUpload, randomId],
	);

	const resetDeleteError = () => setDeleteState(OperationStateTypesEnum.NONE);

	const deleteFile = async (attachmentId: string) => {
		if (!deleteFactory) return;
		try {
			setDeleteState(OperationStateTypesEnum.SUBMITTING);

			await deleteFactory(attachmentId).delete();
			setDeleteState(OperationStateTypesEnum.DONE);
			removeFile(attachmentId);
		} catch (error) {
			setDeleteState(OperationStateTypesEnum.ERROR);
		}
	};

	return {
		uploadedFiles,
		handleUpload,
		deleteFile,
		blockCloseModal,
		IS_DELETING,
		IS_UPLOADING,
		IS_DELETING_ERROR,
		resetDeleteError,
	};
}
